import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Assignment as ReportsIcon, Add as AddIcon, Search as SearchIcon, OpenInNew as OpenIcon,
  Link as LinkIcon, DeleteOutline as DeleteIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
} from '@mui/icons-material';
import ReportUpload, { REPORT_TYPE_LABEL, BLOOD_TEST_LABEL, reportKindLabel } from '../components/ReportUpload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
  .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** Open a stored report in a new tab. The file needs the session's token, so it is
 *  fetched here and handed to the tab as a blob rather than linked directly. */
export async function openReportFile(url) {
  const tab = window.open('', '_blank');
  try {
    const res = await axios.get(url, { responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    if (tab) tab.location.href = href; else window.location.href = href;
  } catch (err) {
    if (tab) tab.close();
    toast.error('Could not open that report');
  }
}

/**
 * Every report this office holds — added by its providers and staff, or by patients
 * from their portal — with the visit each is linked to. `?report=<id>` (from a
 * notification) highlights that one.
 */
export default function Reports() {
  const [params] = useSearchParams();
  const focus = params.get('report');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [bloodTest, setBloodTest] = useState('all');
  const [source, setSource] = useState('all');
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState(null);       // { report, visits, session_id }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/reports`);
      setRows(data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => (type === 'all' || r.report_type === type)
      && (type !== 'blood' || bloodTest === 'all' || (r.blood_test || '') === bloodTest)
      && (source === 'all' || (source === 'patient' ? r.uploaded_by_role === 'patient' : r.uploaded_by_role !== 'patient'))
      && (!s || `${r.title} ${r.patient_name} ${r.notes}`.toLowerCase().includes(s)));
  }, [rows, q, type, bloodTest, source]);

  const startLink = async (r) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/reports/patients/${r.patient_id}/visits`);
      setLinking({ report: r, visits: data.visits || [], session_id: r.session_id || '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load this patient’s visits');
    }
  };

  const saveLink = async () => {
    try {
      await axios.patch(`${API_URL}/api/v1/reports/${linking.report.id}`, { session_id: linking.session_id || '' });
      toast.success(linking.session_id ? 'Linked to the visit' : 'Unlinked from the visit');
      setLinking(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change the link');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete “${r.title}” for ${r.patient_name}? This can’t be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/v1/reports/${r.id}`);
      toast.success('Report deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the report');
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><ReportsIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Reports</Typography>
          <Typography variant="body2" color="text.secondary">
            Blood tests, scans and letters — added here or by patients from their portal.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }} onClick={() => setAdding(true)}>
          Add report
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField fullWidth size="small" placeholder="Search by title, patient or notes…" value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
            <TextField select size="small" label="Type" value={type} onChange={(e) => { setType(e.target.value); setBloodTest('all'); }} sx={{ minWidth: 180 }}>
              <MenuItem value="all">All types</MenuItem>
              {Object.entries(REPORT_TYPE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            {type === 'blood' && (
              <TextField select size="small" label="Blood test" value={bloodTest} onChange={(e) => setBloodTest(e.target.value)} sx={{ minWidth: 220 }}>
                <MenuItem value="all">All blood tests</MenuItem>
                {Object.entries(BLOOD_TEST_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                <MenuItem value="">Not specified</MenuItem>
              </TextField>
            )}
            <TextField select size="small" label="Added by" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="all">Anyone</MenuItem>
              <MenuItem value="clinic">This office</MenuItem>
              <MenuItem value="patient">Patients</MenuItem>
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Report date</TableCell>
              <TableCell>Visit</TableCell>
              <TableCell>Added by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : !visible.length ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                {rows.length ? 'No report matches that.' : 'No reports yet. Add one, or patients can upload from their portal.'}
              </TableCell></TableRow>
            ) : visible.map((r) => (
              <TableRow key={r.id} hover selected={r.id === focus}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {r.content_type === 'application/pdf' ? <PdfIcon color="error" fontSize="small" /> : <ImageIcon color="primary" fontSize="small" />}
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                      <Chip size="small" variant="outlined" label={reportKindLabel(r)} sx={{ height: 20, fontSize: 11, mt: 0.25 }} />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{r.patient_name}</TableCell>
                <TableCell>{fmt(r.report_date)}</TableCell>
                <TableCell>
                  {r.session_id
                    ? <Chip size="small" color="primary" icon={<LinkIcon />} label={fmt(r.visit_date)} onClick={() => startLink(r)} />
                    : <Button size="small" startIcon={<LinkIcon />} onClick={() => startLink(r)}>Link to visit</Button>}
                </TableCell>
                <TableCell>
                  {r.uploaded_by_role === 'patient'
                    ? <Chip size="small" color="secondary" label="Patient" />
                    : <Typography variant="body2">{r.uploaded_by_name || '—'}</Typography>}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fmt(r.created_at)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Open">
                    <IconButton size="small" onClick={() => openReportFile(`${API_URL}/api/v1/reports/${r.id}/file`)}><OpenIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {r.can_delete && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReportUpload open={adding} onClose={() => setAdding(false)} onSaved={load} />

      <Dialog open={Boolean(linking)} onClose={() => setLinking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Link to a visit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            “{linking?.report.title}” for {linking?.report.patient_name}
          </Typography>
          <TextField select fullWidth label="Visit" value={linking?.session_id || ''}
            onChange={(e) => setLinking({ ...linking, session_id: e.target.value })}
            helperText={linking && !linking.visits.length ? 'This patient has no completed visits yet.' : ' '}>
            <MenuItem value=""><em>Not linked to a visit</em></MenuItem>
            {(linking?.visits || []).map((v) => (
              <MenuItem key={v.session_id} value={v.session_id}>{fmt(v.date)} — {v.doctor_name || 'Visit'}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLinking(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveLink}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
